import React, { useEffect, useRef, useState, useCallback } from "react";
import ContentLayout from "../layouts/ContentLayout";
import CardCalendar from "../components/CardCalendar";
import DetailAgenda from "../components/DetailAgenda";
import { apiFetch, getJubileesCached } from "../utils/wkCommon";
import { getYearGanZhiByPreciseLiChun } from "../utils/lunarGanZhi";
import { Link } from "react-router-dom";
import { isZh } from "../utils/language";

/* ===================== HELPERS ===================== */
function monthIdxFromDate(d) {
  return d.getFullYear() * 12 + d.getMonth();
}

function monthIdxToDate(idx) {
  return new Date(Math.floor(idx / 12), idx % 12, 1);
}

function isoYmdUTC(d) {
  return d.toISOString().slice(0, 10);
}

function daysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}

/* ===================== CONFIG ===================== */
const PREV_MONTH_LIMIT = -6;
const NEXT_MONTH_LIMIT = 12;
const SCROLL_ADJUST_PX = 420;

/* ===================== COMPONENT ===================== */
export default function AgendaCalendar() {
  const today = new Date();
  const baseMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const baseIdx = monthIdxFromDate(baseMonth);

  const MIN_MONTH_IDX = baseIdx + PREV_MONTH_LIMIT;
  const MAX_MONTH_IDX = baseIdx + NEXT_MONTH_LIMIT;

  /* ---------- STATE ---------- */
  const [months, setMonths] = useState([baseIdx]);
  const [headerMonth, setHeaderMonth] = useState(baseMonth);

  const [lunarMap, setLunarMap] = useState({});
  const [agendaMap, setAgendaMap] = useState({});
  const [jubilees, setJubilees] = useState([]);

  const [daysMap, setDaysMap] = useState({});
  const [dayKeysOrdered, setDayKeysOrdered] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selectedAgenda, setSelectedAgenda] = useState(null);

  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  const lunarLoaded = useRef(new Set());
  const agendaLoaded = useRef(new Set());
  const jubileesLoaded = useRef(false);
  const loadingNext = useRef(false);

  /* ===================== FETCH LUNAR PER MONTH ===================== */
  const fetchLunarMonth = useCallback(async (monthIdx) => {
    if (lunarLoaded.current.has(monthIdx)) return;
    lunarLoaded.current.add(monthIdx);

    const first = monthIdxToDate(monthIdx);
    const y = first.getFullYear();
    const m = first.getMonth();

    const start = new Date(Date.UTC(y, m, 1));
    const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));

    const qs = new URLSearchParams();
    qs.append("filters[dateInt][$gte]", start.toISOString());
    qs.append("filters[dateInt][$lte]", end.toISOString());
    qs.append("pagination[pageSize]", "50");

    const res = await apiFetch(`/api/lunar-dates?${qs.toString()}`);
    const items = Array.isArray(res?.data) ? res.data : [];

    setLunarMap((prev) => {
      const next = { ...prev };
      items.forEach((it) => {
        const r = it.attributes ?? it;
        if (r.dateInt) {
          const dateKey = r.dateInt.slice(0, 10);

          next[dateKey] = {
            ...r,

            // 🔥 override GanZhi dengan versi presisi
            lunarGanZhi: getYearGanZhiByPreciseLiChun(dateKey)
          };
        }
      });
      return next;
    });
  }, []);

  /* ===================== FETCH AGENDA PER MONTH ===================== */
  const fetchAgendaMonth = useCallback(async (monthIdx) => {
    if (agendaLoaded.current.has(monthIdx)) return;
    agendaLoaded.current.add(monthIdx);

    const first = monthIdxToDate(monthIdx);
    const start = new Date(Date.UTC(first.getFullYear(), first.getMonth(), 1));
    const end = new Date(
      Date.UTC(first.getFullYear(), first.getMonth() + 1, 0, 23, 59, 59, 999)
    );

    const qs = new URLSearchParams();
    qs.append("filters[agendaDate][$gte]", start.toISOString());
    qs.append("filters[agendaDate][$lte]", end.toISOString());
    qs.append("pagination[pageSize]", "500");

    // ⬇️ penting untuk CardAgenda
    qs.append("populate[0]", "fotang_external");
    qs.append("populate[1]", "area");
    qs.append("populate[2]", "area.region");
    qs.append("populate[3]", "agenda_book");
    qs.append("populate[4]", "agenda_pre_reg_form");

    const res = await apiFetch(`/api/agendas?${qs.toString()}`);
    const items = Array.isArray(res?.data) ? res.data : [];

    setAgendaMap((prev) => {
      const next = { ...prev };
      items.forEach((it) => {
        const a = it.attributes ?? it;
        if (!a.agendaDate) return;
        const key = a.agendaDate.slice(0, 10);
        if (!next[key]) next[key] = [];
        next[key].push(it.attributes ? { id: it.id, ...it.attributes } : it);
      });
      return next;
    });
  }, []);

  /* ===================== FETCH JUBILEES ONCE ===================== */
  const fetchJubileesOnce = useCallback(async () => {
    if (jubileesLoaded.current) return;
    jubileesLoaded.current = true;

    const res = await getJubileesCached();
    const items = Array.isArray(res?.data) ? res.data : [];
    setJubilees(items.map((it) => (it.attributes ? { id: it.id, ...it.attributes } : it)));
  }, []);

  /* ===================== BUILD DAYSMAP ===================== */
  useEffect(() => {
    const newMap = {};
    const keys = [];

    months.forEach((idx) => {
      const first = monthIdxToDate(idx);
      const y = first.getFullYear();
      const m = first.getMonth();
      const total = daysInMonth(y, m);

      for (let d = 1; d <= total; d++) {
        const current = new Date(Date.UTC(y, m, d));
        const key = isoYmdUTC(current);
        const lunar = lunarMap[key];
        const dayEvents = [];

        if (agendaMap[key]) {
          agendaMap[key].forEach((a) => dayEvents.push(a));
        }

        const matches = [];

        if (lunar && (lunar.lunarDay === 1 || lunar.lunarDay === 15)) {
          if (
            (lunar.lunarMonth === 1 && lunar.lunarDay === 1) ||
            (lunar.lunarMonth === 3 && lunar.lunarDay === 15) ||
            (lunar.lunarMonth === 6 && lunar.lunarDay === 15) ||
            (lunar.lunarMonth === 7 && lunar.lunarDay === 15) ||
            (lunar.lunarMonth === 8 && lunar.lunarDay === 15) ||
            (lunar.lunarMonth === 9 && lunar.lunarDay === 15) ||
            (lunar.lunarMonth === 11 && lunar.lunarDay === 15)
          ) {
            // skip
          } else {
            matches.push({
              id: `auto-${key}`,
              subject: lunar.lunarDay==1 ? "農曆初一" : "農曆十五",
              jubilee: true,
              jubilee_meta: {
                jubileeLunar: true,
                jubileeMonth: lunar.lunarMonth,
                jubileeDay: lunar.lunarDay,
                tataKrama: "初一十五禮",
                xianGong: "五盤",
              },
            });
          }
        }

        const gm = current.getUTCMonth() + 1;
        const gd = current.getUTCDate();

        jubilees.forEach((j) => {
          if (!j.jubileeLunar && j.jubileeMonth === gm && j.jubileeDay === gd) {
            matches.push({
              id: `jub-g-${j.id}-${key}`,
              subject: j.jubileeDesc,
              jubilee: true,
              jubilee_meta: j,
            });
          }
        });

        if (lunar && !lunar.leapMonth) {
          jubilees.forEach((j) => {
            if (j.jubileeLunar && j.jubileeMonth === lunar.lunarMonth && j.jubileeDay === lunar.lunarDay) {
              matches.push({
                id: `jub-l-${j.id}-${key}`,
                subject: j.jubileeDesc,
                jubilee: true,
                jubilee_meta: j,
              });
            }
          });
        }

        matches.forEach((ev) => dayEvents.unshift(ev));

        if (dayEvents.length > 0) {
          newMap[key] = dayEvents;
          keys.push(key);
        }
      }
    });

    keys.sort();
    setDaysMap(newMap);
    setDayKeysOrdered(keys);
  }, [months, lunarMap, agendaMap, jubilees]);

  /* ===================== INIT ===================== */
  useEffect(() => {
    setLoading(true);
    fetchJubileesOnce();
    fetchLunarMonth(baseIdx);
    fetchAgendaMonth(baseIdx).finally(() => setLoading(false));
  }, [baseIdx, fetchAgendaMonth, fetchJubileesOnce, fetchLunarMonth]);

  /* ===================== AUTO LOAD NEXT ===================== */
  useEffect(() => {
    if (!sentinelRef.current || !listRef.current) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || loadingNext.current) return;
          loadingNext.current = true;

          setMonths((prev) => {
            const next = prev[prev.length - 1] + 1;
            if (next > MAX_MONTH_IDX) return prev;
            fetchLunarMonth(next);
            fetchAgendaMonth(next);
            return [...prev, next];
          });

          setTimeout(() => (loadingNext.current = false), 120);
        });
      },
      { root: listRef.current, rootMargin: "200px" }
    );

    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchAgendaMonth, fetchLunarMonth, MAX_MONTH_IDX]);

  /* ===================== LOAD PREV ===================== */
  const loadPrevMonth = () => {
    setMonths((prev) => {
      const p = prev[0] - 1;
      if (p < MIN_MONTH_IDX) return prev;
      fetchLunarMonth(p);
      fetchAgendaMonth(p);
      return [p, ...prev];
    });

    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop += SCROLL_ADJUST_PX;
    });
  };

  const detailNode = selectedAgenda ? (
    <DetailAgenda
      selectedAgenda={selectedAgenda}
      onClose={() => setSelectedAgenda(null)}
      editUrl={
        selectedAgenda?.documentId
          ? `/agenda/edit/${encodeURIComponent(selectedAgenda.documentId)}`
          : ""
      }
    />
  ) : null;

  const headerActions = (
    <Link
      to="/GanZhiCalendar"
      className="ganzhi-header-btn"
      title={isZh() ? "干支曆與節氣" : "Kalender GanZhi"}
      aria-label={isZh() ? "干支曆與節氣" : "Kalender GanZhi"}
    >
      <img
        src="/icons/GanZhiCalendar.png"
        alt={isZh() ? "干支曆與節氣" : "Kalender GanZhi"}
      />
    </Link>
  );

  /* ===================== RENDER ===================== */
  return (
    <>
      <style>
      {`
      .ganzhi-header-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 0.75rem;

        background: rgba(255,255,255,0.6);   /* lebih terang dari header */
        border: 1px solid rgba(255,255,255,0.35);

        transition: all 0.2s ease;
      }

      .ganzhi-header-btn img {
        width: 1.4rem;
        height: 1.4rem;
        filter: none; /* biarkan warna asli */
      }

      .ganzhi-header-btn:hover {
        background: rgba(255,255,255,0.5);
        transform: translateY(-1px);
      }

      .ganzhi-header-btn:active {
        transform: scale(0.95);
      }
      `}
      </style>

    <ContentLayout
      titleNode="Agenda Calendar"
      headerActions={headerActions}
      hideHeaderActions={true}   // penting supaya fallback button tidak muncul
      mainNode={
        <>
          <CardCalendar
            daysMap={daysMap}
            dayKeysOrdered={dayKeysOrdered}
            lunarMap={lunarMap}
            loading={loading}
            headerMonth={headerMonth}
            listRef={listRef}
            sentinelRef={sentinelRef}
            onLoadPrevMonth={loadPrevMonth}
            onOpenDetail={(ev) => setSelectedAgenda(ev)}
          />
        </>
      }
      detailNode={detailNode}
      onCloseDetail={() => setSelectedAgenda(null)}
      hideLangSwitch={false}
    />
    </>
  );
}
